import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, BadgeCheck, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Pagination, usePaged } from "@/components/Pagination";
import { useAuth } from "@/lib/auth";

const GOLD = "#D4AF37";
const LABELS = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Very good", 5: "Excellent" };

/** Read-only stars; supports halves (4.5). */
export function Stars({ value = 0, size = 14, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const fill = Math.max(0, Math.min(1, value - (n - 1)));
        return (
          <span key={n} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} className="absolute inset-0 text-neutral-300" fill="currentColor" strokeWidth={0}/>
            {fill > 0 && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star size={size} style={{ color: GOLD }} fill="currentColor" strokeWidth={0}/>
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

const initials = (name) => (name || "C").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

export function ProductReviews({ product }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["reviews", product.id],
    queryFn: () => api.get(`/products/${product.id}/reviews`).then((r) => r.data),
    initialData: { reviews: product.reviews || [], avg: product.rating_avg || 0, count: product.rating_count || 0, distribution: null },
    initialDataUpdatedAt: 0,
  });
  const reviews = data?.reviews || [];
  const count = data?.count || 0;
  const avg = data?.avg || 0;
  const dist = data?.distribution || reviews.reduce((d, r) => ({ ...d, [r.rating]: (d[r.rating] || 0) + 1 }), {});
  const mine = user ? reviews.find((r) => r.user_id === user.id) : null;
  const staff = user && ["super_admin", "major_admin", "admin"].includes(user.role);

  const [editing, setEditing] = React.useState(false);
  const [rating, setRating] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const formRef = React.useRef(null);

  React.useEffect(() => {
    if (mine && editing) { setRating(mine.rating); setTitle(mine.title || ""); setBody(mine.body || ""); }
  }, [mine, editing]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["reviews", product.id] }); qc.invalidateQueries({ queryKey: ["product", product.slug] }); qc.invalidateQueries({ queryKey: ["shop-all"] }); };

  const startWrite = () => {
    if (!user) { toast("Sign in to write a review"); nav(`/login?redirect=/product/${product.slug}`); return; }
    setEditing(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please choose a star rating");
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: product.id, rating, title: title.trim() || null, body: body.trim() });
      toast.success(mine ? "Your review was updated" : "Thank you for your review!");
      setEditing(false); setRating(0); setTitle(""); setBody("");
      refresh();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(r.user_id === user?.id ? "Delete your review?" : `Delete ${r.user_name}'s review?`)) return;
    try { await api.delete(`/reviews/${r.id}`); toast.success("Review deleted"); refresh(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const shown = hover || rating;
  const pg = usePaged(reviews, 5, { scrollTarget: "reviews" });

  return (
    <section id="reviews" className="mt-16 sm:mt-20 scroll-mt-28" data-testid="reviews-section">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Customer reviews</div>
          <h2 className="h2-editorial">Ratings & Reviews</h2>
        </div>
        {!editing && (
          <button onClick={startWrite} className="btn-silk rounded-[10px]" data-testid="write-review">
            {mine ? <><Pencil size={13} className="mr-2"/>Edit your review</> : "Write a review"}
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12">
        {/* Summary */}
        <div className="bg-white rounded-2xl border p-6 h-fit" data-testid="review-summary">
          <div className="flex items-end gap-3 mb-2">
            <span className="text-5xl font-semibold text-neutral-900 leading-none">{count ? avg.toFixed(1) : "–"}</span>
            <span className="text-neutral-500 pb-1">out of 5</span>
          </div>
          <Stars value={avg} size={18}/>
          <div className="text-sm text-neutral-500 mt-1.5">{count ? `Based on ${count} review${count > 1 ? "s" : ""}` : "No reviews yet"}</div>
          <div className="mt-5 space-y-1.5">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = dist[n] || dist[String(n)] || 0;
              return (
                <div key={n} className="flex items-center gap-2 text-xs text-neutral-600">
                  <span className="w-6 flex items-center gap-0.5">{n}<Star size={10} fill={GOLD} strokeWidth={0}/></span>
                  <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${count ? (c / count) * 100 : 0}%`, background: GOLD }}/>
                  </div>
                  <span className="w-6 text-right">{c}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          {/* Write / edit form */}
          {editing && (
            <form ref={formRef} onSubmit={submit} className="bg-white rounded-2xl border p-5 sm:p-6 mb-6" data-testid="review-form">
              <h3 className="font-semibold mb-3">{mine ? "Edit your review" : "Write a review"}</h3>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex gap-1" onMouseLeave={() => setHover(0)} role="radiogroup" aria-label="Your rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            onMouseEnter={() => setHover(n)} onClick={() => setRating(n)} data-testid={`star-${n}`}
                            className="p-0.5 transition-transform hover:scale-110">
                      <Star size={30} strokeWidth={1.5} style={{ color: n <= shown ? GOLD : "#d4d4d4" }} fill={n <= shown ? GOLD : "none"}/>
                    </button>
                  ))}
                </div>
                <span className="text-sm text-neutral-600 min-w-[80px]">{shown ? LABELS[shown] : "Tap to rate"}</span>
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Headline (optional) — e.g. Beautiful colour, soft silk"
                     className="w-full border border-neutral-300 rounded-lg p-3 text-sm mb-3 focus:outline-none focus:border-[#0E4D34]" data-testid="review-title"/>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={2000} placeholder="Tell other shoppers about the fabric, colour, fit and drape (optional)"
                        className="w-full border border-neutral-300 rounded-lg p-3 text-sm mb-4 focus:outline-none focus:border-[#0E4D34]" data-testid="review-body"/>
              <div className="flex gap-3">
                <button type="submit" disabled={busy} className="btn-silk rounded-[10px] disabled:opacity-50" data-testid="submit-review">{busy ? "Saving…" : mine ? "Update review" : "Submit review"}</button>
                <button type="button" onClick={() => { setEditing(false); setRating(0); }} className="btn-outline-silk rounded-[10px]">Cancel</button>
              </div>
            </form>
          )}

          {/* List */}
          {reviews.length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <Stars value={0} size={20}/>
              <p className="text-neutral-600 mt-3">No reviews yet. Bought this piece? Be the first to share your experience.</p>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200 bg-white rounded-2xl border" data-testid="review-list">
              {pg.items.map((r) => (
                <li key={r.id} className="p-5 sm:p-6" data-testid="review-item">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E8F3EE] text-[#0E4D34] flex items-center justify-center text-sm font-semibold shrink-0">{initials(r.user_name)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-neutral-900">{r.user_name}</span>
                        {r.verified && <span className="inline-flex items-center gap-1 text-[11px] text-[#0E4D34] bg-[#E8F3EE] rounded-full px-2 py-0.5"><BadgeCheck size={12}/>Verified buyer</span>}
                        {user && r.user_id === user.id && <span className="text-[11px] text-neutral-500">(you)</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Stars value={r.rating} size={14}/>
                        <span className="text-xs text-neutral-500">{fmtDate(r.updated_at || r.created_at)}</span>
                      </div>
                      {r.title && <div className="font-semibold text-neutral-900 mt-2">{r.title}</div>}
                      {r.body && <p className="text-sm text-neutral-700 mt-1 leading-relaxed whitespace-pre-line break-words">{r.body}</p>}
                    </div>
                    {user && (r.user_id === user.id || staff) && (
                      <button onClick={() => remove(r)} aria-label="Delete review" className="p-1.5 text-neutral-400 hover:text-[#991B1B]" data-testid="delete-review"><Trash2 size={15}/></button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Pagination {...pg.props} label="reviews" className="!mt-5"/>
        </div>
      </div>
    </section>
  );
}
